import { useCallback, useEffect, useState } from 'react';
import { useBatchedCallback } from '@wix/da-hooks/pkg/useBatchedCallback';
import { useStream, useNormalizedStream, useFetchOnScrollNearBottom } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { PapiNcMessageObjectSchema } from '../redux/normalizr/schemas';
function useHasScrolled() {
    const [hasScrolled, setHasScrolled] = useState(false);
    const scrollCallback = useCallback((scrollTop)=>{
        if (scrollTop > 0 && !hasScrolled) {
            setHasScrolled(true);
        }
    }, [
        hasScrolled
    ]);
    return [
        hasScrolled,
        scrollCallback
    ];
}
/**
 * Combines all the disparate scroll handlers we need for a notification feed
 */ function useFeedScrolling(streamInfo) {
    const [hasScrolled, hasScrolledCallback] = useHasScrolled();
    const fetchOnScrollToBottomCallback = useFetchOnScrollNearBottom(streamInfo, // Make sure you don't double fetch if you change this
    70);
    return {
        hasScrolled,
        onScroll: useBatchedCallback(hasScrolledCallback, fetchOnScrollToBottomCallback)
    };
}
/**
 * Returns everything we need to know about a feed's stream and everything we need to interact with it.
 */ function useFeedBase({ streamInfo, init }) {
    const { streamId, items, hasMore, fetchNext, isFetching, isInitialized, errorMessage } = streamInfo;
    const isEmpty = items.length === 0 && !isFetching && !hasMore && isInitialized;
    const [mightNotBeScrollable, setMightNotBeScrollable] = useState(false);
    const { hasScrolled, onScroll } = useFeedScrolling(streamInfo);
    useEffect(()=>{
        if (streamId) {
            init();
        }
    }, [
        streamId,
        init
    ]);
    const onNotScrollable = useCallback(()=>{
        if (isInitialized && hasMore && !isFetching && items.length > 0) {
            setMightNotBeScrollable(true);
        }
    }, [
        isInitialized,
        hasMore,
        isFetching,
        items
    ]);
    return {
        isEmpty,
        mightNotBeScrollable,
        hasScrolled,
        onScroll,
        onNotScrollable,
        items,
        hasMore,
        fetchNext,
        isFetching,
        isInitialized,
        errorMessage
    };
}
/**
 * For normalized feed streams. Returns everything we need to know about a feed's stream and everything we need to interact with it.
 */ export function useNormalizedFeed(streamId, init) {
    const streamInfo = useNormalizedStream(streamId, [
        PapiNcMessageObjectSchema
    ]);
    return useFeedBase({
        streamInfo,
        init
    });
}
/**
 * For unnormalized feed streams. Returns everything we need to know about a feed's stream and everything we need to interact with it.
 */ export function useFeed(streamId, init) {
    const streamInfo = useStream(streamId);
    return useFeedBase({
        streamInfo,
        init
    });
}

//# sourceMappingURL=hooks.js.map